// Payment Tracking convergence — the schedule-vs-actual ledger: every
// period's expected vs received vs variance with a RAG status chip.
// Newest period first; the allocation drill-in opens from the row.

import { StatusBadge, Table, TBody, TD, TH, THead, TR } from "@/design-system/components/ui";
import { formatCalendarDate } from "@/utils/datetime";
import type { ServicingLedgerPeriodDto } from "@/api/portfolioAdmin/servicingApi";
import { fmtMoney, PERIOD_STATUS_LABEL, periodStatusTone } from "./servicingFormat";

export function ScheduleLedgerTable({
  periods,
  onOpenAllocation,
  onReverseClick,
}: {
  periods: ServicingLedgerPeriodDto[];
  onOpenAllocation: (paymentId: string) => void;
  /** WS10 — fires the "Reverse payment" action on a v3 payment row. The
   *  backend (ReversalEngineV2) sunset-gates instructed disbursements →
   *  422; the modal surfaces that as "use Adjustment Entry". */
  onReverseClick?: (paymentId: string) => void;
}) {
  const ordered = [...periods].sort((a, b) => b.periodIndex - a.periodIndex);

  return (
    <Table data-testid="schedule-ledger-table">
      <THead>
        <TR>
          <TH>Due date</TH>
          <TH className="text-right">Expected</TH>
          <TH className="text-right">Received</TH>
          <TH className="text-right">Variance</TH>
          <TH>Status</TH>
          <TH>Received on</TH>
          <TH>
            <span className="sr-only">Allocation</span>
          </TH>
        </TR>
      </THead>
      <TBody>
        {ordered.map((p) => {
          const variance = p.receivedAmount - p.expectedAmount;
          const showVariance = p.status !== "upcoming";
          return (
            <TR key={p.periodIndex} data-testid={`ledger-period-${p.periodIndex}`}>
              <TD>
                {formatCalendarDate(p.dueDate)}
                {p.isBalloon && (
                  <span className="ml-space-2">
                    <StatusBadge tone="info">Balloon</StatusBadge>
                  </span>
                )}
              </TD>
              <TD className="text-right tabular-nums">{fmtMoney(p.expectedAmount)}</TD>
              <TD className="text-right tabular-nums">
                {p.receivedAmount > 0 ? fmtMoney(p.receivedAmount) : "—"}
              </TD>
              <TD
                className={`text-right tabular-nums ${
                  showVariance && variance < -0.004 ? "text-status-error" : ""
                }`}
              >
                {showVariance && Math.abs(variance) > 0.004 ? fmtMoney(variance) : "—"}
              </TD>
              <TD>
                <StatusBadge tone={periodStatusTone(p.status)}>
                  {PERIOD_STATUS_LABEL[p.status]}
                </StatusBadge>
                {p.source === "legacy" && (
                  <span className="ml-space-2 text-caption text-gray-mid">pre-v3</span>
                )}
              </TD>
              <TD>{p.receivedDate ? formatCalendarDate(p.receivedDate) : "—"}</TD>
              <TD>
                {p.paymentId && (
                  <div className="flex items-center gap-space-3">
                    <button
                      type="button"
                      className="text-small text-gold-shadow hover:underline"
                      onClick={() => onOpenAllocation(p.paymentId!)}
                      data-testid={`ledger-allocation-${p.periodIndex}`}
                    >
                      View allocation
                    </button>
                    {onReverseClick && p.source === "v3" && (
                      <button
                        type="button"
                        className="text-small text-status-error hover:underline"
                        onClick={() => onReverseClick(p.paymentId!)}
                        data-testid={`ledger-reverse-${p.periodIndex}`}
                      >
                        Reverse
                      </button>
                    )}
                  </div>
                )}
              </TD>
            </TR>
          );
        })}
      </TBody>
    </Table>
  );
}
